import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import {
  AreaStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { AreaService } from "src/service/area.service";
import {
  areaLoadErrorAction,
  areaLoadLastModifiedFromApiAction,
  areaLoadListFromApiAction,
  areaLoadSuccessAction,
  areaUnsetKeyStorage,
} from "../actions/area.actions";
import { listArea } from "../selectors/area.selector";
import { currentUserData } from "../selectors/authentication.selectors";

/**
 * Handle generic side ngrx effect from area action
 */
@Injectable()
export class AreaEffects {
  constructor(
    private _actions$: Actions,
    private _areaService: AreaService,
    private _store: Store
  ) { }

  /**
   * Effect who catch main event and unset specific server key storage
   */
  public areaUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map((action) => areaUnsetKeyStorage())
    )
  );

  /**
   * Effet attached to ngrx store init, then start init initialization
   */
  public areaStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AreaStoreAction.AREA_LOAD_INIT_START),
      withLatestFrom(
        this._store.select(currentUserData),
        this._store.select(listArea)
      ),
      filter(
        (combinedObject) =>
          !!(combinedObject[1] as GenericUserData)?.currentUser
      ),
      map((combinedObject: any) => {
        const areaObject: GenericStoreApiData = combinedObject[2];

        if (areaObject?.data) {
          return areaLoadLastModifiedFromApiAction({
            areaObject: areaObject.data,
          });
        } else {
          return areaLoadListFromApiAction();
        }
      })
    )
  );

  /**
   * Load full list of areas effect
   */
  public areaListLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AreaStoreAction.AREA_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._areaService.getAreas().pipe(
          map((areaObject: GenericApiData) =>
            areaLoadSuccessAction({ areaObject })
          ),
          catchError(() => of(areaLoadErrorAction()))
        );
      })
    )
  );

  /**
   * Load full list of area by last modified effect
   */
  public areaListLastModifiedLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(AreaStoreAction.AREA_LOAD_LAST_MODIFIED_FROM_API),
      switchMap((action: any) => {
        return this._areaService.getAreasByLastModified(action.areaObject).pipe(
          map((areaObject: GenericApiData) =>
            areaLoadSuccessAction({ areaObject })
          ),
          catchError(() => of(areaLoadErrorAction()))
        );
      })
    )
  );
}
